import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectV2, setOperations, setStep } from '../v2Upload.slice';
import ToolTable from '../components/ToolTable';
import WizardFooter from '../components/WizardFooter';
import FieldError, { redlineGroup } from '../components/FieldError';
import useFieldErrors from '../useFieldErrors';
import { missingForTools } from '../utils/validation';
import { operationsForFiles } from '../utils/fileHelpers';
import { operationAnswers, seedOperationsFromCam } from '../utils/camSeed';
import { AutofillLegend } from '../components/AutofillMark';
import { useGetActiveBitsQuery } from '@/redux/bit/bit.api.slice';
import useDraftSync from '../useDraftSync';
import { V2Operation } from '../v2Upload.d';
import { STEP_HEADING } from '../components/fileCardStyles';

/** The same rows, in the same order, carrying the same bits — so re-deriving
 * them is a no-op. Identity alone would swallow a CAM hint landing on a row
 * that already existed. */
const sameOperations = (a: V2Operation[], b: V2Operation[]) =>
  a.length === b.length &&
  a.every((op, i) => op.id === b[i].id && op.bits === b[i].bits);

export default function ToolsStep() {
  const dispatch = useDispatch();
  const { operations, files, parsedReady, camExtracted, basics } =
    useSelector(selectV2);
  const [saving, setSaving] = useState(false);
  const { syncModelGroups } = useDraftSync();
  // The picker renders from this same catalog, so a CAM tool resolved against
  // it shows the bit's real name and image rather than a bare id.
  const { data: catalog = [], isLoading: catalogLoading } =
    useGetActiveBitsQuery();

  /**
   * One operation per G-code file, built on arrival and kept in step as files
   * come and go. Existing assignments are carried over, so this never clears a
   * bit the user picked — and a resumed draft, whose operations are restored
   * before the re-parse settles, keeps what came back from the server.
   */
  const seeded = useRef(false);
  useEffect(() => {
    // A row seeded before the catalog lands keeps a type with no bit for good
    if (!parsedReady || catalogLoading) return;
    const rows = operationsForFiles(files, operations);
    // CAM hints go on after the rows exist, and only onto rows with no bit
    // picked yet — so a dropped .art fills the picker in without ever
    // overwriting a choice the user already made.
    const next = seedOperationsFromCam(
      rows,
      files,
      camExtracted,
      catalog,
      basics.material
    );
    seeded.current = true;
    if (!sameOperations(next, operations)) dispatch(setOperations(next));
  }, [
    parsedReady,
    catalogLoading,
    files,
    operations,
    camExtracted,
    catalog,
    basics.material,
    dispatch,
  ]);

  const answers = operationAnswers(
    operations,
    files,
    camExtracted,
    catalog,
    basics.material
  );

  const missing = missingForTools(operations);
  const { bad, props, guard } = useFieldErrors(missing);
  // The gaps are per operation, so the group's own redline follows them.
  const invalidOps = operations
    .filter((op) => bad(`op-${op.id}`))
    .map((op) => op.id);

  const handleContinue = async () => {
    if (!guard()) return;
    setSaving(true);
    const ok = await syncModelGroups();
    setSaving(false);
    if (ok) dispatch(setStep(4));
  };

  return (
    <div className="flex flex-col min-h-full">
      <h2 className={STEP_HEADING}>Select Bits or Laser</h2>
      <p className="text-sm text-muted-foreground mb-3 max-w-[640px]">
        Set the cut order and assign the bit or laser used for each G-code file.
      </p>
      <AutofillLegend show={Object.values(answers).some(Boolean)} />

      <div {...props('tool-groups')}>
        <div
          className={redlineGroup(bad('tool-groups') || invalidOps.length > 0)}
        >
          <ToolTable
            operations={operations}
            files={files}
            invalidOps={invalidOps}
          />
        </div>
        <FieldError
          missing={missing}
          field="tool-groups"
          show={bad('tool-groups')}
        />
      </div>

      <WizardFooter
        onBack={() => dispatch(setStep(2))}
        onContinue={handleContinue}
        busy={saving}
        busyLabel="Saving…"
        backDisabled={saving}
        continueDisabled={false}
      />
    </div>
  );
}
